/** The header numbers — refactoring triage without scrolling the tree.
 *
 *  These are the corpus analysis's own metrics turned into a per-notification
 *  readout: how many branches, how deep, how wide the widest switch, how many
 *  conditions branch on a hardcoded tour id, how many are in-template language
 *  switches. A body scoring high on those three is exactly what the composition
 *  refactor is meant to eliminate.
 *
 *  Fields that are zero are omitted rather than printed as zeros — except the
 *  ones whose absence is itself the answer (`unknown block refs`).
 */
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';

import type { AnatomyStats } from '@/api/notificationAnatomy';

interface Stat {
  label: string;
  value: number | string;
  help: string;
  tone?: 'default' | 'warning' | 'error';
  /** Show even at zero. */
  always?: boolean;
}

export function StructureSummary({
  stats,
  truncated,
  visibleRows,
}: {
  stats: AnatomyStats;
  truncated: boolean;
  visibleRows: number;
}) {
  const items: Stat[] = [
    { label: 'nodes', value: stats.nodes, help: 'Total structural nodes parsed.', always: true },
    { label: 'branches', value: stats.branch_count, help: 'if / elif branches across the whole composition.' },
    { label: 'max depth', value: stats.max_depth, help: 'Deepest structural nesting.' },
    {
      label: 'widest switch',
      value: stats.max_switch,
      help: 'The widest if/elif/else fan-out. Anything over ~5 is a switch pretending to be a condition.',
      tone: stats.max_switch >= 10 ? 'warning' : 'default',
    },
    {
      label: 'tour-id branches',
      value: stats.tour_id_conditions,
      help: 'Conditions comparing against a hardcoded tour id — the corpus motif the refactor targets (a new tour means editing every one).',
      tone: stats.tour_id_conditions > 0 ? 'warning' : 'default',
    },
    {
      label: 'language switches',
      value: stats.language_conditions,
      help: 'In-template language branching — a language policy replaces these.',
      tone: stats.language_conditions > 0 ? 'warning' : 'default',
    },
    { label: 'loops', value: stats.for_count, help: '{% for %} loops.' },
    { label: 'block refs', value: stats.block_refs.length, help: 'Distinct block.* references.' },
    {
      label: 'unknown block refs',
      value: stats.unknown_block_refs.length,
      help: 'block.* names that match nothing visible to this tenant — they render EMPTY, silently.',
      tone: stats.unknown_block_refs.length > 0 ? 'error' : 'default',
      always: true,
    },
    {
      label: 'literal text',
      value: `${stats.text_length.toLocaleString()} chars`,
      help: 'Total literal (non-template) characters.',
    },
  ];

  return (
    <Box>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        {items
          .filter((item) => item.always || (typeof item.value === 'number' ? item.value > 0 : true))
          .map((item) => (
            <Tooltip key={item.label} describeChild title={item.help}>
              <Chip
                size="small"
                label={`${item.label} ${item.value}`}
                color={item.tone === 'default' ? 'default' : item.tone}
                variant={item.tone && item.tone !== 'default' ? 'filled' : 'outlined'}
                sx={{ height: 22, fontVariantNumeric: 'tabular-nums' }}
              />
            </Tooltip>
          ))}
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
        Showing {visibleRows.toLocaleString()} of {stats.nodes.toLocaleString()} nodes — subtrees
        are collapsed by default.
        {truncated && ' The node cap was reached; truncated subtrees are marked in the tree.'}
      </Typography>

      {stats.unknown_block_refs.length > 0 && (
        <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.5 }}>
          Unknown: {stats.unknown_block_refs.map((r) => `block.${r}`).join(', ')} — these render
          empty at delivery time without raising anything.
        </Typography>
      )}
    </Box>
  );
}
